/**
 * adminCategoriesSlice — Redux Toolkit slice for admin category management.
 *
 * State shape
 * -----------
 * - items: AdminCategoryDTO[]         — current page of categories (list view)
 * - total: number                     — total count from the API
 * - currentPage: number               — current page
 * - perPage: number                   — page size
 * - loading: boolean                  — list fetch in progress
 * - error: AdminCategoriesError | null — last error (preserves full platform envelope)
 * - currentCategory: AdminCategoryDTO | null — category open in the editor
 * - mutating: boolean                 — write operation in progress
 * - allCategories: AdminCategoryDTO[] — flat list for parent selects (from tree fetch)
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only page-level containers do.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createAdminCategory,
  deleteAdminCategory,
  fetchAdminCategories,
  fetchAdminCategory,
  fetchAdminCategoryTree,
  updateAdminCategory,
} from '@dfq/shared/api/admin/categories';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  AdminCategoryCreatePayload,
  AdminCategoryDTO,
  AdminCategoryListParams,
  AdminCategoryUpdatePayload,
} from '@dfq/shared/types/category';

// ---------------------------------------------------------------------------
// Error shape — preserves the full platform error envelope
// ---------------------------------------------------------------------------

export interface AdminCategoriesError {
  message: string;
  severity?: string;
  errors?: Record<string, string[]>;
  errorCode?: string;
  status: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface AdminCategoriesState {
  items: AdminCategoryDTO[];
  total: number;
  currentPage: number;
  perPage: number;
  loading: boolean;
  error: AdminCategoriesError | null;
  currentCategory: AdminCategoryDTO | null;
  mutating: boolean;
  /** Flat list of all categories for parent select dropdowns. */
  allCategories: AdminCategoryDTO[];
}

const initialState: AdminCategoriesState = {
  items: [],
  total: 0,
  currentPage: 1,
  perPage: 25,
  loading: false,
  error: null,
  currentCategory: null,
  mutating: false,
  allCategories: [],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flattenCategoryTree(cats: AdminCategoryDTO[]): AdminCategoryDTO[] {
  return cats.reduce<AdminCategoryDTO[]>((acc, c) => {
    acc.push(c);
    if (c.children.length > 0) acc.push(...flattenCategoryTree(c.children));
    return acc;
  }, []);
}

function toError(err: unknown, fallback: string): AdminCategoriesError {
  if (isApiError(err)) {
    return {
      message: err.message,
      severity: err.severity,
      errors: err.errors,
      errorCode: err.errorCode,
      status: err.status,
    };
  }
  const msg = err instanceof Error ? err.message : fallback;
  return { message: msg, status: 0 };
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchAdminCategoriesThunk = createAsyncThunk(
  'adminCategories/fetchList',
  async (params: AdminCategoryListParams = {}, { rejectWithValue }) => {
    try {
      return await fetchAdminCategories(params);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to fetch categories'));
    }
  },
);

export const fetchAdminCategoryTreeThunk = createAsyncThunk(
  'adminCategories/fetchTree',
  async (rootId: number | null | undefined, { rejectWithValue }) => {
    try {
      return await fetchAdminCategoryTree(rootId);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to fetch category tree'));
    }
  },
);

export const fetchAdminCategoryThunk = createAsyncThunk(
  'adminCategories/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await fetchAdminCategory(id);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to fetch category'));
    }
  },
);

export const createAdminCategoryThunk = createAsyncThunk(
  'adminCategories/create',
  async (payload: AdminCategoryCreatePayload, { rejectWithValue }) => {
    try {
      return await createAdminCategory(payload);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to create category'));
    }
  },
);

export const updateAdminCategoryThunk = createAsyncThunk(
  'adminCategories/update',
  async (
    { id, ...payload }: { id: number } & AdminCategoryUpdatePayload,
    { rejectWithValue },
  ) => {
    try {
      return await updateAdminCategory(id, payload);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to update category'));
    }
  },
);

export const deleteAdminCategoryThunk = createAsyncThunk(
  'adminCategories/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAdminCategory(id);
      return id;
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to delete category'));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const adminCategoriesSlice = createSlice({
  name: 'adminCategories',
  initialState,
  reducers: {
    clearCurrentCategory(state) {
      state.currentCategory = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchAdminCategoriesThunk (list)
    builder
      .addCase(fetchAdminCategoriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminCategoriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.categories;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
        state.perPage = action.payload.perPage;
      })
      .addCase(fetchAdminCategoriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as AdminCategoriesError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch categories', status: 0 };
      });

    // fetchAdminCategoryTreeThunk (all categories for parent select)
    builder
      .addCase(fetchAdminCategoryTreeThunk.fulfilled, (state, action) => {
        state.allCategories = flattenCategoryTree(action.payload);
      });

    // fetchAdminCategoryThunk (single)
    builder
      .addCase(fetchAdminCategoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminCategoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCategory = action.payload;
      })
      .addCase(fetchAdminCategoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as AdminCategoriesError | undefined) ??
          { message: action.error.message ?? 'Failed to fetch category', status: 0 };
      });

    // createAdminCategoryThunk
    builder
      .addCase(createAdminCategoryThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(createAdminCategoryThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentCategory = action.payload;
      })
      .addCase(createAdminCategoryThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminCategoriesError | undefined) ??
          { message: action.error.message ?? 'Failed to create category', status: 0 };
      });

    // updateAdminCategoryThunk
    builder
      .addCase(updateAdminCategoryThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(updateAdminCategoryThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentCategory = action.payload;
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(updateAdminCategoryThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminCategoriesError | undefined) ??
          { message: action.error.message ?? 'Failed to update category', status: 0 };
      });

    // deleteAdminCategoryThunk
    builder
      .addCase(deleteAdminCategoryThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(deleteAdminCategoryThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.items = state.items.filter((c) => c.id !== action.payload);
        if (state.currentCategory?.id === action.payload) {
          state.currentCategory = null;
        }
      })
      .addCase(deleteAdminCategoryThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error =
          (action.payload as AdminCategoriesError | undefined) ??
          { message: action.error.message ?? 'Failed to delete category', status: 0 };
      });
  },
});

export const { clearCurrentCategory, clearError: clearAdminCategoryError } =
  adminCategoriesSlice.actions;

export const adminCategoriesReducer = adminCategoriesSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface AdminCategoriesRootState {
  adminCategories: AdminCategoriesState;
}

export const selectAdminCategories = (
  state: AdminCategoriesRootState,
): AdminCategoryDTO[] => state.adminCategories.items;

export const selectAdminCategoriesLoading = (
  state: AdminCategoriesRootState,
): boolean => state.adminCategories.loading;

export const selectAdminCategoriesError = (
  state: AdminCategoriesRootState,
): AdminCategoriesError | null => state.adminCategories.error;

export const selectCurrentAdminCategory = (
  state: AdminCategoriesRootState,
): AdminCategoryDTO | null => state.adminCategories.currentCategory;

export const selectAdminCategoriesMutating = (
  state: AdminCategoriesRootState,
): boolean => state.adminCategories.mutating;

export const selectAdminCategoriesTotal = (
  state: AdminCategoriesRootState,
): number => state.adminCategories.total;

export const selectAdminCategoriesPage = (
  state: AdminCategoriesRootState,
): number => state.adminCategories.currentPage;

export const selectAdminCategoriesPerPage = (
  state: AdminCategoriesRootState,
): number => state.adminCategories.perPage;

export const selectAllAdminCategories = (
  state: AdminCategoriesRootState,
): AdminCategoryDTO[] => state.adminCategories.allCategories;
